/**
 * Middleware for the Call Management & UX feature.
 *
 * Listens for WebSocket events from InterpreterQueueService and
 * dispatches corresponding Redux actions.
 */
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { setPersistentItem } from '../vrs-auth/storage';
import { callWaitingIncoming, callOnHold, callOffHold, chatMessageReceived, chatHistoryLoaded, preferencesUpdated, p2pTargetDnd } from './actions';
import { CALL_TRANSFER_INITIATED, CONFERENCE_INVITE, CONFERENCE_ADD_RINGING, CONFERENCE_ADD_OFFLINE, CONFERENCE_REMOVED, CONFERENCE_PARTICIPANT_REMOVED } from './actionTypes';
let _initialized = false;
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    if (!_initialized && queueService) {
        initializeCallManagementListeners(store);
        _initialized = true;
    }
    return result;
});
function initializeCallManagementListeners(store) {
    if (!queueService?.on) {
        return;
    }
    // Call Waiting
    queueService.on('callWaitingIncoming', (data) => {
        store.dispatch(callWaitingIncoming(data));
    });
    queueService.on('callWaitingResponded', (_data) => {
        // Handled by reducer via the action dispatched from the component
    });
    // Call Hold
    queueService.on('callOnHold', (data) => {
        store.dispatch(callOnHold(data));
    });
    queueService.on('callOffHold', (data) => {
        store.dispatch(callOffHold(data));
    });
    // Call Transfer
    queueService.on('callTransferInitiated', (data) => {
        store.dispatch({ type: CALL_TRANSFER_INITIATED, data });
    });
    // Conference
    queueService.on('conferenceInvite', (data) => {
        store.dispatch({ type: CONFERENCE_INVITE, data });
    });
    queueService.on('conferenceAddRinging', (data) => {
        store.dispatch({ type: CONFERENCE_ADD_RINGING, data });
    });
    queueService.on('conferenceAddOffline', (data) => {
        store.dispatch({ type: CONFERENCE_ADD_OFFLINE, data });
    });
    queueService.on('conferenceRemoved', (data) => {
        store.dispatch({ type: CONFERENCE_REMOVED, data });
    });
    queueService.on('conferenceParticipantRemoved', (data) => {
        store.dispatch({ type: CONFERENCE_PARTICIPANT_REMOVED, data });
    });
    // Chat
    queueService.on('chatMessage', (data) => {
        store.dispatch(chatMessageReceived(data));
    });
    queueService.on('chatHistory', (data) => {
        store.dispatch(chatHistoryLoaded(data));
    });
    // Preferences
    queueService.on('preferencesUpdated', (data) => {
        store.dispatch(preferencesUpdated(data));
        if (typeof data.camera_default_off === 'boolean') {
            setPersistentItem('vrs_incoming_video_enabled', JSON.stringify(!data.camera_default_off));
        }
        // Apply dark mode on <body>
        if (data.dark_mode) {
            applyDarkMode(data.dark_mode);
        }
    });
    // P2P Target DND
    queueService.on('p2pTargetDnd', (data) => {
        store.dispatch(p2pTargetDnd(data));
    });
}
/**
 * Apply dark mode class to the document body.
 */
function applyDarkMode(mode) {
    if (typeof document === 'undefined') {
        return;
    }
    const body = document.body;
    body.classList.remove('vrs-dark', 'vrs-light');
    if (mode === 'dark') {
        body.classList.add('vrs-dark');
    }
    else if (mode === 'light') {
        body.classList.add('vrs-light');
    }
    else {
        // system — detect OS preference
        const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
        body.classList.add(prefersDark ? 'vrs-dark' : 'vrs-light');
    }
}
// Export for use by the dark mode component
export { applyDarkMode };
